/** Shared pulse bone for project detail skeletons. */
function Bone({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded bg-[var(--p-color-bg-surface-tertiary)] ${className}`}
    />
  );
}

export function ProjectActivityTabSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading activity">
      <Bone className="h-12 w-full rounded-md" />
      <div className="rounded-md border border-[var(--p-color-border)] p-4 space-y-3">
        <Bone className="h-5 w-2/3" />
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((i) => (
            <Bone key={i} className="h-8 w-16 rounded-md" />
          ))}
        </div>
        <Bone className="h-9 w-full" />
        <Bone className="h-24 w-full" />
        <div className="flex justify-end gap-2">
          <Bone className="h-9 w-20" />
          <Bone className="h-9 w-20" />
        </div>
      </div>
    </div>
  );
}

export function ProjectNotesTabSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading notes">
      <Bone className="h-20 w-full rounded-md" />
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex gap-3 rounded-md border border-amber-100 bg-[#fffbeb]/80 p-3"
        >
          <Bone className="h-8 w-8 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Bone className="h-3 w-28" />
            <Bone className="h-3 w-full" />
            <Bone className="h-3 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProjectEmailTabSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading email">
      <div className="overflow-hidden rounded-lg border border-[var(--p-color-border)]">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 border-b border-[var(--p-color-border)] px-4 py-3"
          >
            <Bone className="h-3 w-10 shrink-0" />
            <Bone className="h-3 flex-1" />
          </div>
        ))}
        <div className="space-y-2 p-4">
          <Bone className="h-4 w-40" />
          <Bone className="h-36 w-full" />
        </div>
        <div className="flex items-center justify-between border-t border-[var(--p-color-border)] px-3 py-2.5">
          <div className="flex gap-2">
            <Bone className="h-8 w-8 rounded-md" />
            <Bone className="h-8 w-8 rounded-md" />
            <Bone className="h-8 w-8 rounded-md" />
          </div>
          <Bone className="h-8 w-24 rounded-md" />
        </div>
      </div>
      <div className="space-y-2">
        <Bone className="h-4 w-28" />
        {[1, 2].map((i) => (
          <Bone key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

export function ProjectFilesTabSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading files">
      <div className="flex items-center justify-between">
        <Bone className="h-5 w-16" />
        <Bone className="h-8 w-24" />
      </div>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-md border border-[var(--p-color-border)] px-3 py-2.5"
        >
          <Bone className="h-9 w-9 shrink-0 rounded" />
          <div className="min-w-0 flex-1 space-y-2">
            <Bone className="h-3 w-1/2" />
            <Bone className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProjectProductsTabSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading products">
      <Bone className="h-5 w-24" />
      <div className="overflow-hidden rounded-md border border-[var(--p-color-border)]">
        <div className="flex gap-4 border-b border-[var(--p-color-border)] bg-[var(--p-color-bg-surface-secondary)] px-3 py-2">
          <Bone className="h-3 w-24" />
          <Bone className="h-3 w-16" />
          <Bone className="h-3 w-16" />
          <Bone className="h-3 w-20" />
        </div>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex gap-4 border-b border-[var(--p-color-border)] px-3 py-3 last:border-0"
          >
            <Bone className="h-3 w-32" />
            <Bone className="h-3 w-12" />
            <Bone className="h-3 w-16" />
            <Bone className="h-3 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectFocusListSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading focus">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-[var(--p-color-border)] px-3 py-2.5 space-y-2"
        >
          <Bone className="h-3 w-2/5" />
          <Bone className="h-3 w-3/5" />
        </div>
      ))}
    </div>
  );
}

export function ProjectHistoryTimelineSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <ul
      className="relative space-y-4 pl-8"
      aria-busy="true"
      aria-label="Loading history"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="relative">
          <span className="absolute -left-8 flex h-6 w-6 items-center justify-center">
            <Bone className="h-6 w-6 rounded-full" />
          </span>
          <div className="rounded-md border border-[var(--p-color-border)] px-3 py-2.5 space-y-2">
            <Bone className="h-3 w-1/3" />
            <Bone className="h-3 w-full" />
            <Bone className="h-3 w-2/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export type ProjectTabSkeletonId =
  | "activity"
  | "notes"
  | "email"
  | "files"
  | "products";

export function ProjectTabContentSkeleton({
  tab,
}: {
  tab: ProjectTabSkeletonId;
}) {
  switch (tab) {
    case "activity":
      return <ProjectActivityTabSkeleton />;
    case "notes":
      return <ProjectNotesTabSkeleton />;
    case "email":
      return <ProjectEmailTabSkeleton />;
    case "files":
      return <ProjectFilesTabSkeleton />;
    case "products":
      return <ProjectProductsTabSkeleton />;
    default:
      return <ProjectActivityTabSkeleton />;
  }
}
